import type { SearchSelectOption } from '../components/ui/SearchSelect';

export const ROLE_LABELS: Record<string, string> = {
    ADMIN: 'Quản trị viên',
    MANAGER: 'Quản lý',
    TEACHER: 'Giáo viên',
    ASSISTANT: 'Trợ giảng',
    STAFF: 'Nhân viên',
};

const DAY_SHORT: Record<string, string> = {
    monday: 'T2', tuesday: 'T3', wednesday: 'T4', thursday: 'T5', friday: 'T6', saturday: 'T7', sunday: 'CN',
    'Thứ 2': 'T2', 'Thứ 3': 'T3', 'Thứ 4': 'T4', 'Thứ 5': 'T5', 'Thứ 6': 'T6', 'Thứ 7': 'T7', 'Chủ nhật': 'CN',
};
const DAY_ORDER = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

const nameOf = (value: unknown) => (value && typeof value === 'object' ? (value as { name?: string }).name : value) as string | undefined;

// "T2, T4 17:00–19:00", or "T2 17:00–19:00 · T5 18:00–20:00" when the times differ.
export function scheduleSummary(entries: Array<{ day: string; start?: string; end?: string }> | undefined) {
    const list = (entries ?? [])
        .map(entry => ({ day: DAY_SHORT[entry.day] ?? entry.day, time: entry.start && entry.end && entry.start !== '00:00' ? `${entry.start}–${entry.end}` : '' }))
        .sort((a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day));
    if (list.length === 0) return '';
    const times = new Set(list.map(item => item.time));
    if (times.size === 1) return `${list.map(item => item.day).join(', ')} ${list[0].time}`.trim();
    return list.map(item => `${item.day} ${item.time}`.trim()).join(' · ');
}

export interface PickableClass {
    id: string;
    name: string;
    subject?: unknown;
    campus?: unknown;
    teacher?: string | null;
    scheduleEntries?: Array<{ day: string; start?: string; end?: string }>;
    enrolled?: number;
    capacity?: number;
    isActive?: boolean;
}

// Classes grouped by campus, active ones first, with subject, schedule, teacher and head count on the second line.
export function classPickerOptions(classes: PickableClass[], { showCapacity = false } = {}): SearchSelectOption[] {
    return [...classes]
        .sort((a, b) => (nameOf(a.campus) ?? '').localeCompare(nameOf(b.campus) ?? '', 'vi')
            || Number(b.isActive !== false) - Number(a.isActive !== false)
            || a.name.localeCompare(b.name, 'vi', { numeric: true }))
        .map(item => {
            const inactive = item.isActive === false;
            const full = showCapacity && Boolean(item.capacity) && (item.enrolled ?? 0) >= (item.capacity ?? 0);
            const description = [
                nameOf(item.subject),
                scheduleSummary(item.scheduleEntries),
                item.teacher ? `GV ${item.teacher}` : '',
                showCapacity && item.capacity ? `${item.enrolled ?? 0}/${item.capacity} HS${full ? ' (đủ)' : ''}` : '',
                inactive ? 'Đã nghỉ' : '',
            ].filter(Boolean).join(' · ');
            return {
                value: item.id,
                label: item.name,
                description,
                group: nameOf(item.campus) || 'Chưa có cơ sở',
                muted: inactive || full,
            };
        });
}

export interface PickablePerson {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    role?: string;
    isActive?: boolean;
}

export const personName = (person: PickablePerson) => `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email || '';

// Staff by name, with the role in Vietnamese; email name and phone only match the search.
export function personPickerOptions(people: PickablePerson[]): SearchSelectOption[] {
    return [...people]
        .sort((a, b) => Number(b.isActive !== false) - Number(a.isActive !== false) || personName(a).localeCompare(personName(b), 'vi'))
        .map(person => ({
            value: person.id,
            label: personName(person),
            description: [person.role ? ROLE_LABELS[person.role] ?? person.role : '', person.isActive === false ? 'Đã khoá' : ''].filter(Boolean).join(' · '),
            // Only the part before @: the shared domain would match everyone.
            keywords: [person.email?.split('@')[0], person.phone].filter(Boolean).join(' '),
            muted: person.isActive === false,
        }));
}

// For lists that only carry id, name and campusId: same grouping by campus.
export function classOptionsByCampus(
    classes: Array<{ id: string; name: string; campusId: string }>,
    campuses: Array<{ id: string; name: string }>,
    campusId?: string,
): SearchSelectOption[] {
    const campusName = new Map(campuses.map(campus => [campus.id, campus.name]));
    return classPickerOptions(classes
        .filter(item => !campusId || item.campusId === campusId)
        .map(item => ({ id: item.id, name: item.name, campus: campusName.get(item.campusId) })));
}
